package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.AddEmployeeViewModel
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.CaptionCapitalization
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberIsWebPortraitLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p18
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.ui.theme.LocalTheme.p64
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.views.CountryCodePickerBottomSheet
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun AddEmployeeView(viewModel: AddEmployeeViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current

    if (PlatformHelper.isWeb && !rememberIsWebPortraitLayout()) {
        AddEmployeeWebView(viewModel = viewModel)
    } else {
    UIView(
        viewModel = viewModel,
        navigationContent = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = p16, end = p16, top = p12, bottom = p20)
            ) {
                Texts.TitleMedium(
                    text = sharedString(SR.strings.add_employee),
                    color = AppTheme.current.colors.onSurface,
                    modifier = Modifier
                        .fillMaxWidth()
                        .align(Alignment.Center),
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .align(Alignment.CenterStart),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(p16)
                ) {
                    Box(
                        modifier = Modifier
                            .size(p48)
                            .clip(CircleShape)
                            .background(AppTheme.current.colors.surfaceContainerLowest)
                            .noRippleClickable {
                                keyboardController?.hide()
                                viewModel.navCallbacks.backButtonCallback()
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        androidx.compose.foundation.Image(
                            painter = painterResource(SRC.images.arrow_back),
                            contentDescription = null,
                            contentScale = ContentScale.Fit,
                            modifier = Modifier.size(width = p10, height = p18),
                            colorFilter = androidx.compose.ui.graphics.ColorFilter.tint(AppTheme.current.colors.primary)
                        )
                    }
                    Spacer(modifier = Modifier.weight(1f))
                }
            }
        }
    ) { state ->
        val uiState by state

        AddEmployeeContent(uiState = uiState, viewModel = viewModel, modifier = Modifier.fillMaxSize())
    }
    }

    val uiStateForSheet by viewModel.uiState.subscribeAsState()
    CountryCodePickerBottomSheet(
        isVisible = uiStateForSheet.showDialCodePicker,
        countries = uiStateForSheet.countries,
        selectedDialCode = uiStateForSheet.dialCode,
        onCountrySelected = { country ->
            viewModel.selectDialCodeCountry(country.dialCode)
        },
        onDismiss = viewModel::hideDialCodePicker
    )
}

@Composable
internal fun AddEmployeeContent(
    uiState: com.example.mystybag.appCore.model.pages.AddEmployeeUIState,
    viewModel: AddEmployeeViewModel,
    modifier: Modifier
) {
    var roleMenuExpanded by remember { mutableStateOf(false) }
    val webLandscape = PlatformHelper.isWeb && !rememberIsWebPortraitLayout()

    Column(modifier = modifier) {
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .scrollEdgeFade(showTop = false)
                .padding(horizontal = p16),
            verticalArrangement = Arrangement.spacedBy(p24)
        ) {
            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.email,
                label = stringResource(SR.strings.email_address),
                errorText = uiState.emailErrorMsg,
                onValueChange = viewModel::onEmailChange,
                isError = uiState.emailIsError,
                inputType = TextFieldType.EMAIL,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email, imeAction = ImeAction.Next)
            )

            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.firstName,
                label = stringResource(SR.strings.first_name_label),
                errorText = uiState.firstNameErrorMsg,
                onValueChange = viewModel::onFirstNameChange,
                isError = uiState.firstNameIsError,
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next)
            )

            DefaultCircleShapedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiState.lastName,
                label = stringResource(SR.strings.last_name_label),
                errorText = uiState.lastNameErrorMsg,
                onValueChange = viewModel::onLastNameChange,
                isError = uiState.lastNameIsError,
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Next)
            )

            // Contact number – same as EditEmployeeView
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(p12)
            ) {
                Texts.BodyMedium(
                    text = stringResource(SR.strings.your_mobile_number),
                    modifier = Modifier.fillMaxWidth()
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(p8)
                ) {
                    Box(modifier = Modifier.weight(0.32f)) {
                        DefaultCircleShapedTextField(
                            value = uiState.dialCode,
                            onValueChange = { },
                            label = stringResource(SR.strings.dial_code),
                            modifier = Modifier.fillMaxWidth(),
                            readOnly = true,
                            enabled = true,
                            canFocus = false,
                            errorText = uiState.dialCodeErrorMsg,
                            isError = uiState.dialCodeIsError,
                            backgroundColor = AppTheme.current.colors.surface,
                            borderColor = AppTheme.current.colors.outline,
                            trailingIcon = {
                                androidx.compose.foundation.Image(
                                    painter = painterResource(
                                        when {
                                            uiState.showDialCodePicker -> SRC.images.combobox_arrow_down_green
                                            uiState.dialCode.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                            else -> SRC.images.combobox_arrow_right_gray
                                        }
                                    ),
                                    modifier = Modifier.size(p16),
                                    contentDescription = null
                                )
                            }
                        )
                        Box(
                            modifier = Modifier
                                .matchParentSize()
                                .clickable { viewModel.showDialCodePicker() }
                        )
                    }
                    DefaultCircleShapedTextField(
                        value = uiState.phoneNumber,
                        onValueChange = viewModel::onPhoneNumberChange,
                        label = stringResource(SR.strings.phone_number_label),
                        modifier = Modifier.weight(0.68f),
                        errorText = uiState.phoneNumberErrorMsg,
                        isError = uiState.phoneNumberIsError,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone, imeAction = ImeAction.Next)
                    )
                }
            }

            // Role dropdown – same styling as EditEmployeeView
            Box(modifier = Modifier.fillMaxWidth()) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(p1, AppTheme.current.colors.outline, RoundedCornerShape(p12))
                        .clickable { roleMenuExpanded = true },
                    shape = RoundedCornerShape(p12),
                    colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surface),
                    elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(start = p16, end = p16, top = p18, bottom = p18),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column(modifier = Modifier.weight(1f)) {
                            Texts.LabelSmall(
                                text = "Role",
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                            Texts.BodyLarge(
                                text = when (uiState.role) {
                                    "OWNER" -> stringResource(SR.strings.role_owner)
                                    "MANAGER" -> stringResource(SR.strings.role_manager)
                                    else -> stringResource(SR.strings.role_staff)
                                },
                                color = AppTheme.current.colors.onSurface
                            )
                        }
                        androidx.compose.foundation.Image(
                            painter = painterResource(SRC.images.combobox_arrow_down_green),
                            modifier = Modifier.size(p16),
                            contentDescription = null
                        )
                    }
                }
                DropdownMenu(
                    expanded = roleMenuExpanded,
                    onDismissRequest = { roleMenuExpanded = false },
                    containerColor = AppTheme.current.colors.surface,
                    shape = RoundedCornerShape(p20)
                ) {
                    val roleOptions = listOf(
                        "STAFF" to stringResource(SR.strings.role_staff),
                        "MANAGER" to stringResource(SR.strings.role_manager),
                        "OWNER" to stringResource(SR.strings.role_owner)
                    )
                    roleOptions.forEachIndexed { index, (roleValue, roleLabel) ->
                        DropdownMenuItem(
                            text = { Texts.BodyMedium(text = roleLabel) },
                            onClick = {
                                viewModel.onRoleChange(roleValue)
                                roleMenuExpanded = false
                            }
                        )
                        if (index < roleOptions.lastIndex) {
                            HorizontalDivider(
                                thickness = p1,
                                color = AppTheme.current.colors.outline
                            )
                        }
                    }
                }
            }

            // Permissions section – two boolean toggles applied when this staff logs in
            AddEmployeeSectionHeader(title = stringResource(SR.strings.permissions))

            EmployeePermissionRow(
                label = stringResource(SR.strings.permission_can_create_bags),
                checked = uiState.canCreateBags,
                onCheckedChange = viewModel::onCanCreateBagsChange
            )
            HorizontalDivider(
                modifier = Modifier.fillMaxWidth(),
                thickness = p1,
                color = AppTheme.current.colors.outline
            )
            EmployeePermissionRow(
                label = stringResource(SR.strings.permission_can_view_revenue),
                checked = uiState.canViewRevenue,
                onCheckedChange = viewModel::onCanViewRevenueChange
            )

            // Section header STORE – same as EditEmployeeView
            AddEmployeeSectionHeader(title = "STORE")

            if (uiState.locationsErrorMsg.isNotEmpty()) {
                Texts.BodySmall(
                    text = uiState.locationsErrorMsg,
                    color = AppTheme.current.colors.error
                )
            }
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(0.dp)
            ) {
                uiState.locations.forEachIndexed { index, location ->
                    if (index > 0) {
                        HorizontalDivider(
                            modifier = Modifier.fillMaxWidth(),
                            thickness = p1,
                            color = AppTheme.current.colors.outline
                        )
                    }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = p64)
                            .clickable { viewModel.toggleLocation(location.id) }
                            .padding(vertical = p20, horizontal = p16),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(p16)
                    ) {
                        Checkbox(
                            checked = location.id in uiState.selectedLocationIds,
                            onCheckedChange = { viewModel.toggleLocation(location.id) },
                            modifier = Modifier.size(p24),
                            colors = CheckboxDefaults.colors(
                                checkedColor = AppTheme.current.colors.primary,
                                uncheckedColor = AppTheme.current.colors.outline
                            )
                        )
                        Texts.BodyLarge(
                            text = location.title?.takeIf { it.isNotBlank() }
                                ?: location.cityName?.takeIf { it.isNotBlank() }
                                ?: location.address?.takeIf { it.isNotBlank() }
                                ?: "Location #${location.id}",
                            color = AppTheme.current.colors.onSurface,
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            }
        }

        DefaultButton(
            modifier = Modifier
                .fillMaxWidth(if (webLandscape) 0.5f else 1f)
                .align(Alignment.CenterHorizontally)
                .height(p56)
                .padding(horizontal = if (webLandscape) 0.dp else p16)
                .padding(bottom = if (webLandscape) 32.dp else p8),
            caption = sharedString(SR.strings.add_employee),
            capitalization = CaptionCapitalization.None,
            contentPadding = if (webLandscape) PaddingValues(horizontal = p16, vertical = 0.dp) else null,
            callback = { viewModel.onSubmit() }
        )
    }
}

@Composable
private fun AddEmployeeSectionHeader(title: String) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p12)
    ) {
        Texts.LabelLarge(
            text = title,
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier.height(p16)
        )
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth(),
            thickness = p1,
            color = AppTheme.current.colors.outline
        )
    }
}

/**
 * Row used in the permissions section of Add/Edit employee.
 * Layout matches the STORE checkbox rows: 64dp min height, label left, control right.
 */
@Composable
internal fun EmployeePermissionRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onCheckedChange(!checked) }
            .padding(vertical = p8, horizontal = p16),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p16)
    ) {
        Texts.BodyLarge(
            text = label,
            color = AppTheme.current.colors.onSurface,
            modifier = Modifier.weight(1f)
        )
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = AppTheme.current.colors.onPrimary,
                checkedTrackColor = AppTheme.current.colors.primary,
                uncheckedThumbColor = AppTheme.current.colors.outline,
                uncheckedTrackColor = AppTheme.current.colors.surfaceContainerLowest
            )
        )
    }
}

